import useComputed from '@/hooks/use-computed';
import { immerable } from "immer";
import { useState } from 'react';
import api from '@/services/api/dataExchangeRelation.js';
import orgAPI from '@/services/api/org.js';
import dataElementAPI from '@/services/api/dataElements.js';
import dataElementVersionAPI from '@/services/api/dataSchemaVersioning.js';
import pitstopsAPI from '@/services/api/sys.js';
import { useFeature } from '@/hooks/use-feature';
import { featureConstant } from '@/constants/configuration/feature';

class DataExchange {
	[immerable] = true
	constructor(params) {
		this.dataExchangeRelationshipId = params.dataExchangeRelationshipId
		this.dataExchangeRelationshipInvitesId = params.dataExchangeRelationshipInvitesId
		this.dataElementId = params.dataElementId
		this.dataElementName = params.dataElementName
		this.senderOrgId = params.senderOrgId
		this.senderOrgName = params.senderOrgName
		this.senderEmail = params.senderEmail
		this.serviceProviderRelationshipId = params.serviceProviderRelationshipId
		this.serviceProviderRelationshipInvitesId = params.serviceProviderRelationshipInvitesId
		this.serviceProviderOrgId = params.serviceProviderOrgId
		this.serviceProviderOrgName = params.serviceProviderOrgName
		this.serviceProviderEmail = params.serviceProviderEmail
		this.serviceProviderRelationshipStatus = params.serviceProviderRelationshipStatus
		this.receiverOrgId = params.receiverOrgId
		this.receiverOrgName = params.receiverOrgName
		this.receiverEmail = params.receiverEmail
		this.modified = params.modified
		this.dataExchangeRelationshipStatus = params.dataExchangeRelationshipStatus
		this.initiater = params.initiater
		this.flow = params.flow
		this.ownSystems = params.ownSystems
		this.$selected = false
		this.pitstopId = params.pitstopId
		this.ccToContributor = params.ccToContributor
		this.contributorOrgId = params.contributorOrgId
		this.contributorPitstopName = params.contributorPitstopName
		this.contributorSystemId = params.contributorSystemId
	}
}

export const modifyResponse =(response) =>{
	const modifiedResponse = response.flatMap((record) => {
		if (record?.ownSystems?.length > 0) {
			return record?.ownSystems?.map((system) => ({
				...record,
				pitstopId: system,
			}));
		} else {
			return {
				...record,
				pitstopId: '',
			};
		}
	});
	return modifiedResponse
}

const useRelationshipSetup = () => {

	const [dataExchangeListing, setDataExchangeListing] = useComputed([])
	const [allOrgData, setAllOrgData] = useState([]);
	const [serviceProviderRelation, setServiceProviderRelation] = useState([]);
	const [dataElement, setDataElement] = useState([]);
	const [pitstops, setPitstops] = useState([]);

	const [dataExchangeLoading, setDataExchangeLoading] = useState(false)
	const [orgDataLoading, setOrgDataLoading] = useState(false)
	const [serviceProviderRelationLoading, setServiceProviderRelationLoading] = useState(false)
	const [dataElementLoading, setDataElementLoading] = useState(false)
	const [pitstopsLoading, setPitstopsLoading] = useState(false)

	const [error, setError] = useState();

	const isDSVEnabled = useFeature(featureConstant.DSV_ENABLED);


	async function fetchDataExchange() {
		setDataExchangeLoading(true);
		const [error, response] = await api.index();

		setDataExchangeLoading(false)

		if (error) {
			return setError('Data Exchange API has error')
		}

		const modifiedResponse=	modifyResponse(response)

		const serializedData = modifiedResponse?.map(entry => serialize(entry))
		const newItems = serializedData.map(params => new DataExchange(params))

		setDataExchangeListing(newItems);
		return newItems;
	}

	async function fetchOrgData() {
		setOrgDataLoading(true)
		const [error, data] = await orgAPI.index();

		setOrgDataLoading(false)
		if (error) {
			return setError('Org API has error')
		}

		if (!Array.isArray(data.items)) {
			return setError('Org API is not an array')
		}

		setAllOrgData(data.items);
	}

	async function fetchServiceProviderRelation() {
		setServiceProviderRelationLoading(true)
		const [error, response] = await api.getServiceProviderRelationships();
		setServiceProviderRelationLoading(false)

		if (error) {
			return setError('Solution Provider Relationship API has error')
		}

		setServiceProviderRelation(response);
	}

	async function fetchDataElements() {
		setDataElementLoading(true);
		const [deError, deResponse] = await dataElementAPI.index();

		if (deError) {
			setDataElementLoading(false);
			return setError('Data Elements API has error')
		}

		// Legacy path when DSV is disabled (DataElementVersion table may not be
		// synced yet on this backend) — preserve pre-DSV behavior of listing
		// all DataElements unfiltered.
		if (!isDSVEnabled) {
			setDataElementLoading(false);
			return setDataElement(deResponse);
		}

		// Only DEs with at least one ACTIVE version are eligible for new data
		// exchange relationships — DRAFT/DEPRECATED/RETIRED versions cannot back
		// a new relationship. Fetch the active versions and filter the picker.
		const [vError, vResponse] = await dataElementVersionAPI.index('status=ACTIVE&all=true');
		setDataElementLoading(false);

		if (vError) {
			// Fail open: surface the DE list unfiltered if version lookup fails,
			// rather than blocking the page entirely.
			return setDataElement(deResponse);
		}

		const versions = Array.isArray(vResponse?.items)
			? vResponse.items
			: Array.isArray(vResponse)
				? vResponse
				: [];
		const activeIds = new Set(versions.map((v) => v.dataElementId));
		setDataElement(deResponse.filter((de) => activeIds.has(de.dataElementId)));
	}

	async function fetchPitstops() {
		setPitstopsLoading(true)
		const [error, data] = await pitstopsAPI.getPitstops()
		setPitstopsLoading(false)

		if (error) {
			return setError('Pitstops API has error')
		}

		setPitstops(data);
	}

	function serialize(entry) {
		return {
			dataExchangeRelationshipId: entry.dataExchangeRelationshipId,
			dataExchangeRelationshipInvitesId: entry.dataExchangeRelationshipInvitesId,
			dataElementId: entry.dataElementId,
			dataElementName: entry.dataElementName,
			senderOrgId: entry.senderOrgId,
			senderOrgName: entry.senderOrgName,
			senderEmail: entry.senderEmail,
			serviceProviderRelationshipId: entry.serviceProviderRelationshipId,
			serviceProviderRelationshipInvitesId: entry.serviceProviderRelationshipInvitesId,
			serviceProviderOrgId: entry.serviceProviderOrgId,
			serviceProviderOrgName: entry.serviceProviderOrgName,
			serviceProviderRelationshipStatus: entry.serviceProviderRelationshipStatus,
			serviceProviderEmail: entry.serviceProviderEmail,
			receiverOrgId: entry.receiverOrgId,
			receiverOrgName: entry.receiverOrgName,
			receiverEmail: entry.receiverEmail,
			modified: entry.modified,
			dataExchangeRelationshipStatus: entry.dataExchangeRelationshipStatus,
			initiater: entry.initiater,
			flow: entry.flow,
			ownSystems: entry.ownSystems,
			pitstopId: entry.pitstopId,
			ccToContributor: entry.ccToContributor,
			contributorOrgId: entry.contributorOrgId,
			contributorPitstopName: entry.contributorPitstopName,
			contributorSystemId: entry.contributorSystemId,
		}
	}

	return {
		dataExchangeListing,
		allOrgData,
		serviceProviderRelation,
		dataElement,
		pitstops,
		modifyResponse,
		fetchDataExchange,
		fetchServiceProviderRelation,
		fetchOrgData,
		fetchDataElements,
		fetchPitstops,
		dataExchangeLoading,
		orgDataLoading,
		serviceProviderRelationLoading,
		dataElementLoading,
		pitstopsLoading,
		error
	}

}

export default useRelationshipSetup;
